import CloseIcon from "@mui/icons-material/Close"; // MUI Close Icon
import { IconButton, Modal, Button as MuiButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FEATURES } from "../../constants/features";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import useSegmentSettings from "../../hooks/useSegmentSetting";
import { resetToWheelBgToInitialState } from "../../redux/actions/wheelWallpaper";
import { hideCreateWheelPopup } from "../../redux/reducers/createWheelPopup";
import {
  setAutomaticResizing,
  setIsClonable,
  setIsLive,
  setIsLocked,
  setVisibility,
  setWheelColorPalette,
  setWheelName,
} from "../../redux/reducers/segmentBuilder";
import { openUpgradePopup } from "../../redux/reducers/upgradePopup";
import { savePalette } from "../../services/api";
import {
  useCreateWheelV2,
  useDeleteWheelV2,
  useAddEmails,
} from "../../services/mutations";
import Button from "../Button";
import NewAIBuilder from "../NewAIBuilder";
import AiBuilderUpgradeModal from "../NewAIBuilder/Sections/Wait";
import {
  ButtonContainer,
  ModalContainer,
  ModalContent,
  PaginationContainer,
  PaginationDot,
  SkipButton,
  TopCircle,
} from "./CreateWheelPopup.styled";
import AIBuilder from "./Sections/AIBuilder";

import WheelCloning from "./Sections/WheelCloning";
import WheelColorPalette from "./Sections/WheelColorPalette";
import WheelColorTheme from "./Sections/WheelColorTheme";
import WheelInfo from "./Sections/WheelInfo";
import WheelLocking from "./Sections/WheelLocking";
import WheelLockingSettingsModal from "./Sections/WheelLockingSettingsModal";
import WheelVisibility from "./Sections/WheelVisibility";
import { setColorPaletteDefault } from "../../redux/reducers/colorPalette";
import { getActiveSubscriptionData } from "../../redux/selectors/subscriptionSelectors";

function CreateWheelPopup({ explorePage = false }) {
  const { isOpen } = useSelector((state) => state.createWheelPopup);
  const [isAiBuilderSelected, setIsAiBuilderSelected] = useState(false);
  const [wheelPaletteOption, setWheelPaletteOption] = useState(1);
  const [selectedWheelPalette, setSelectedWheelPalette] = useState(null);
  const [wheelNameError, setWheelNameError] = useState(false);
  const selectedPaletteTab = useSelector(
    (state) => state.colorPalette.selectedPaletteTab
  );

  const nav = useNavigate();
  const {
    isEligible: isEligibleForAIWheel,
    isCheckingEligibility: checkingAIWheel,
  } = useIfUserEligible(FEATURES.AI_WHEEL_GENERATOR, { explorePage });

  const {
    isEligible: isEligibleForPaletteGenerator,
    isCheckingEligibility: checkingPalette,
  } = useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR, { explorePage });

  const createWheel = useCreateWheelV2();
  const addEmailsMutation = useAddEmails();
  const wheelInformation = useSelector((state) => state.segmentBuilder);
  const wheelWallpaperSettings = useSelector(
    (state) => state.wheelWallpaperReducer
  );
  const isLocked =
    useSelector(
      (state) =>
        wheelInformation?.history[wheelInformation?.currentIndex]?.is_locked
    ) || 0;
  const { generatePayload } = useSegmentSettings() || {};
  const [page, setPage] = useState(0); // Tracks current page
  const [showAiBuilderModal, setShowAiBuilderModal] = useState(false); // Tracks current page
  const [showLockSettingsModal, setShowLockSettingsModal] = useState(false); // Tracks lock settings modal
  const [lockSettings, setLockSettings] = useState(null); // Stores lock settings from modal
  const [pendingEmails, setPendingEmails] = useState([]); // Stores emails to add after wheel creation

  const dispatch = useDispatch();
  const { data: currentPlan } = useSelector(
    getActiveSubscriptionData
  );
  const showUpgradeButton =
    (page == 4 && !isEligibleForAIWheel && isAiBuilderSelected) ||
    (page == 5 && !isEligibleForPaletteGenerator && wheelPaletteOption == 2) ||
    (page == 4 && isAiBuilderSelected && currentPlan?.sub_tier === 0);

  useEffect(() => {
    dispatch(resetToWheelBgToInitialState());
    dispatch(setAutomaticResizing(false)); // Default to Equal segment sizing
  }, [isOpen, dispatch]);

  useEffect(() => {
    dispatch(hideCreateWheelPopup());
  }, [dispatch]);

  const handleClose = () => {
    dispatch(hideCreateWheelPopup());
    setShowAiBuilderModal(false);
    setShowLockSettingsModal(false);
    setLockSettings(null);
    setPendingEmails([]);
    setPage(0);
    setWheelPaletteOption(1);
    dispatch(setColorPaletteDefault({ paletteType: "wheel" }));
    setIsAiBuilderSelected(false);
    dispatch(setWheelName(""));
    dispatch(setVisibility(1));
    dispatch(setIsLocked(0));
    dispatch(setIsClonable(false));
    dispatch(setIsLive(false));
    dispatch(setAutomaticResizing(false)); // Default to Equal segment sizing
  };

  const handleMutation = (mutationFunc, payload) => {
    mutationFunc(payload, {
      onSuccess: async (res) => {
        if (res.data.success) {
          const wheelId = res.data.data.id;

          // If there are pending emails, add them first

          if (pendingEmails.length > 0) {
            try {
              await new Promise((resolve, reject) => {
                addEmailsMutation.mutate(
                  { wheelId, emails: pendingEmails },
                  {
                    onSuccess: () => {
                      resolve();
                    },
                    onError: (error) => {
                      toast.error("Error adding emails: " + error.message);
                      reject(error);
                    },
                  }
                );
              });
            } catch (error) {
              // Continue to builder even if email adding fails
              console.error("Error adding emails:", error);
            }
          }

          toast.success("Wheel created successfully");
          window.location.href = `/builder/${wheelId}`;
          handleClose();
        }
      },
      onError: (err) => {
        console.log({ err });
        toast.error(err?.message); // Show error toast regardless
      },
    });
  };

  const validateWheelName = () => {
    const wheelName =
      wheelInformation?.history[wheelInformation?.currentIndex]?.wheel_name;
    if (!wheelName || wheelName.trim() === "") {
      setWheelNameError(true);
      return false;
    }
    setWheelNameError(false);
    return true;
  };

  const isFreeTier = currentPlan?.sub_tier === 0;

  const handleSkipGuide = () => {
    if (!validateWheelName()) {
      setPage(0);
      return;
    }

    if (isFreeTier) {
      setPage(4); // AI Builder
      return;
    }

    setPage(5); // Wheel Color Theme
  };

  const handleSaveWheel = async () => {
    if (!validateWheelName()) {
      setPage(0);
      return;
    }

    try {
      if (isAiBuilderSelected) {
        setShowAiBuilderModal(true);
      } else {
        let savedPaletteData = null;

        if (wheelPaletteOption == 2 && selectedWheelPalette) {
          const response = await handleSavePalette();
          if (!response?.data?.success) {
            throw new Error("Failed to save palette");
          }
          savedPaletteData = response.data.data;
        } else {
          dispatch(
            setWheelColorPalette({
              paletteData: null,
              paletteId: null,
            })
          );
        }

        const currentState =
          wheelInformation?.history[wheelInformation?.currentIndex] || {};

        // Merge lock settings if they exist
        const wheelData = {
          ...currentState,
          palette_id: savedPaletteData?.id || currentState.palette_id,
        };

        if (lockSettings) {
          wheelData.lock_type = lockSettings;
        }

        const payload = generatePayload(wheelData, wheelWallpaperSettings);

        const mutationFunction = createWheel.mutate;
        handleMutation(mutationFunction, payload);
      }
    } catch (error) {
      console.error("Error saving wheel:", error);
      toast.error("Failed to create wheel. Please try again.");
    }
  };

  const handleSavePalette = async () => {
    const TAB_ENUMS = {
      0: 3, // Prompt
      1: 2, // Image
      2: 1, // Color
    };
    try {
      const response = await savePalette({
        id:
          typeof selectedWheelPalette.id === "string"
            ? undefined
            : selectedWheelPalette.id,
        colors: selectedWheelPalette.colors,
        palette_name: selectedWheelPalette.paletteName,
        type: 2,
        usage_type: 1,
        tab_type: TAB_ENUMS[selectedPaletteTab],
      });

      if (response.data.success) {
        const paletteId = response.data.data.id;
        // Update Redux state
        dispatch(
          setWheelColorPalette({
            paletteData: selectedWheelPalette,
            paletteId,
          })
        );
        toast.success("Palette created for your wheel.");
      }
      return response;
    } catch (error) {
      console.error("Error saving palette:", error);
      throw error;
    }
  };

  const handleNext = () => {
    if (showUpgradeButton) {
      dispatch(openUpgradePopup());
      return;
    }
    if (!validateWheelName()) {
      setPage(0);
      return;
    }

    if (page == 2 && isLocked == 1) {
      // Show lock settings modal when user selects locked and clicks next
      setShowLockSettingsModal(true);
      return;
    }

    if (page == 4 && isAiBuilderSelected) {
      setShowAiBuilderModal(true);
      return;
    }

    if (page == 5) {
      if (wheelPaletteOption == 2) {
        setPage((prev) => prev + 1);
      } else {
        handleSaveWheel();
      }
    } else if (page < pages.length - 1) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (page > 0) {
      setPage((prev) => prev - 1);
    }
  };

  const handleLockSettingsApply = ({ lock_type, emails }) => {
    setLockSettings({ ...lock_type });
    setPendingEmails(emails || []);

    // Continue to next page after applying lock settings
    setPage((prev) => prev + 1);
  };

  const handleLockSettingsClose = () => {
    setShowLockSettingsModal(false);
  };

  const pages = [
    <WheelInfo
      error={wheelNameError}
      clearError={() => setWheelNameError(false)}
    />,
    <WheelVisibility />,
    <WheelLocking />,
    <WheelCloning />,
    <AIBuilder
      isAiBuilderSelected={isAiBuilderSelected}
      setAiBuilder={setIsAiBuilderSelected}
    />,
    <WheelColorTheme
      wheelPaletteOption={wheelPaletteOption}
      setWheelPaletteOption={setWheelPaletteOption}
    />,
    <WheelColorPalette setSelectedWheelPalette={setSelectedWheelPalette} />,
  ];

  if (showAiBuilderModal) {
    if (!isEligibleForAIWheel) {
      return (
        <AiBuilderUpgradeModal
          isOpen={showAiBuilderModal}
          onClose={() => {
            setShowAiBuilderModal(false);
          }}
        />
      );
    } else {
      return (
        <NewAIBuilder
          isOpen={showAiBuilderModal}
          onClose={() => {
            setShowAiBuilderModal(false);
          }}
          onSuccess={handleClose}
          onCancelWizard={handleClose}
        />
      );
    }
  }

  if (showLockSettingsModal) {
    return (
      <WheelLockingSettingsModal
        isOpen={showLockSettingsModal}
        handleClose={handleLockSettingsClose}
        onApplySettings={handleLockSettingsApply}
        handlePrevious={() => {
          setShowLockSettingsModal(false);
          setPage(2); // Go back to wheel locking page
        }}
      />
    );
  }

  return (
    <div>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalContainer
          style={
            page == pages.length - 1
              ? {
                  width: "64vw",
                  minWidth: "840px",
                  height: "80vh",
                }
              : null
          }
        >
          {/* Close Button (Cross) */}
          <IconButton
            edge="start"
            color="inherit"
            size="large"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <TopCircle />
          <ModalContent>{pages[page]}</ModalContent>

          <ButtonContainer>
            <SkipButton onClick={handleSkipGuide}>
              Skip Guide
            </SkipButton>
            {/* Cancel or Previous Button */}
            {page === 0 ? (
              <MuiButton
                variant="text"
                sx={{ color: "black", textTransform: "none", width: "90px" }}
                onClick={handleClose}
              >
                Cancel
              </MuiButton>
            ) : (
              <MuiButton
                variant="text"
                onClick={handlePrevious}
                sx={{ color: "black", textTransform: "none", width: "90px" }}
                disabled={page === 0}
              >
                Previous
              </MuiButton>
            )}
            {/* Next or Build Button */}
            {page === pages.length - 1 ||
            (wheelPaletteOption == 1 && page == pages.length - 2) ? (
              <Button primary onClick={() => handleSaveWheel()}>
                Build
              </Button>
            ) : (
              <Button primary onClick={handleNext}>
                {showUpgradeButton ? "Upgrade Now" : "Next"}
              </Button>
            )}
          </ButtonContainer>

          <PaginationContainer>
            {pages.slice(0, -1).map((_, index) => (
              <PaginationDot
                key={index}
                active={index <= page}
                current={
                  index === page ||
                  (page == pages.length - 1 && index == pages.length - 2)
                }
              />
            ))}
          </PaginationContainer>
        </ModalContainer>
      </Modal>
    </div>
  );
}

export default CreateWheelPopup;
